import { ref } from 'vue';
import type { RouteLocationNormalizedLoaded, Router } from 'vue-router';
import type { Store } from 'vuex';
import { makeRequest } from '@/graphs/index';
import { findStickerCollectionsForSticker } from '@/graphs/gql';
import type {
  StickerCollectionForSticker,
  StickerCollectionStickerRef,
} from '@/data/stickerCollection.types';
import {
  buildStickerPathWithAttribution,
  copyStickerNavQueryParams,
  pickStickerAttributionQuery,
} from '@/utils/stickerShareUrl';

export function useStickerCollectionsOnDetail(store: Store<any>) {
  const stickerCollections = ref<StickerCollectionForSticker[]>([]);
  const collectionsLoading = ref(false);
  let latestRequest = 0;

  async function fetchStickerCollections(stickerId: string | null | undefined) {
    latestRequest += 1;
    const requestId = latestRequest;

    if (!stickerId) {
      stickerCollections.value = [];
      collectionsLoading.value = false;
      return;
    }

    collectionsLoading.value = true;
    try {
      const isSignedIn = store.getters['users/isMeSignedIn'];
      const context = isSignedIn ? {} : { token: null };
      // Progress of whoever the user is acting as, matching redeemStickerCollectionCode
      const orgId = isSignedIn ? store.getters['actingEntity/orgId'] : null;
      const variables = { stickerId: String(stickerId), ...(orgId ? { orgId } : {}) };

      const response = await makeRequest(
        'query',
        findStickerCollectionsForSticker,
        variables,
        context,
      );

      const list = response?.data?.findStickerCollectionsForSticker;
      if (response?.errors?.length) {
        console.error('findStickerCollectionsForSticker errors:', response.errors);
      }

      // A newer fetch (e.g. the user moved to another sticker) owns the state now.
      if (requestId !== latestRequest) return;
      stickerCollections.value = Array.isArray(list) ? list : [];
    } catch (err) {
      console.error('Failed to fetch sticker collections:', err);
      if (requestId === latestRequest) stickerCollections.value = [];
    } finally {
      if (requestId === latestRequest) collectionsLoading.value = false;
    }
  }

  function navigateToCollectionSticker(
    sticker: StickerCollectionStickerRef,
    currentStickerId: string,
    route: RouteLocationNormalizedLoaded,
    router: Router,
    isEmbedded: boolean,
  ) {
    if (!sticker?.id || sticker.id === currentStickerId) {
      return;
    }

    const slugOrId = sticker.slug || sticker.id;

    if (isEmbedded) {
      const queryParams = copyStickerNavQueryParams(route.query as Record<string, unknown>);
      const queryString = queryParams.toString();
      const queryParam = queryString ? `?${queryString}` : '';
      router.push(`/web/embed/sticker/${slugOrId}${queryParam}`);
      return;
    }

    const path = buildStickerPathWithAttribution(
      slugOrId,
      pickStickerAttributionQuery(route.query as Record<string, unknown>),
    );
    router.push(path);
  }

  return {
    stickerCollections,
    collectionsLoading,
    fetchStickerCollections,
    navigateToCollectionSticker,
  };
}
